import React, { useMemo, useRef, useState } from "react";

import { Box, Image } from "@chakra-ui/react";
import { ReactComponent as ChevronRightIcon } from "assets/icons/table-action-chevron-right.svg";
import { COLORS_MAX_LENGTH_INDEX } from "features/business-compliance/consts";
import {
  ComplianceStatus,
  statusToTextMap,
} from "features/business-compliance/selected-business/ComplianceStatus";
import { getNextColor } from "features/business-compliance/tab-items";
import { formatBusinessDetailsRoute } from "features/businesses/business-details/content";
import { useTableActions } from "hooks/useTableActions";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/constants";
import {
  formatDateToDDMMMYYYY,
  getRandomNumber,
  handleAmountFormat,
  handleTruncateAmount,
} from "utils";
import {
  ONE_NAIRA_IN_KOBO,
  REGISTERED_BUSINESS_COMPANY_TYPE,
  START_INDEX,
  UNREGISTERED_BUSINESS_COMPANY_TYPE,
} from "utils/constants";

import { Pagination } from "ui/pagination";
import { Status } from "ui/status";
import { activeTextToStatus, statusToActiveText } from "ui/status/const";
import { StatusUiColorEnum } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds, TTableRowData } from "ui/table/types";
import { Heading4, Paragraph3, Paragraph4 } from "ui/typography";

import { businessTableColumnCss, businessTypeCss } from "../styles";
import { TableHeaderCTAs } from "./TableHeaderCTAs";
import {
  BUSINESSES_LIST_TABLE_PAGINATION_PARAMS,
  BusinessTypeColorMap,
  columns,
} from "./const";
import { IBusinessListTableProps } from "./types";

const lastColor = "";
export const BusinessListTable = (props: IBusinessListTableProps) => {
  const {
    businesses,
    businessesData,
    isLoadingBusinessesData,
    handleGetBusinessesData,
  } = props;
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const { data: _, ...paginationData } = businessesData;
  const [selectedBusinessIds, setSelectedBusinessIds] =
    useState<TSelectedTableRowIds>({});
  const { handleRefreshTable, isRefreshing } = useTableActions({
    isLoadingTable: isLoadingBusinessesData,
  });

  const handleViewBusinessDetails = (businessRowData: TTableRowData) => {
    if (
      businessRowData.status === "pending" &&
      businessRowData.businessType === "registered"
    ) {
      const continueBusinessRegistrationRoute = formatBusinessDetailsRoute(
        ROUTES.CONTINUE_BUSINESS_REGISTRATION_PAGE,
        businessRowData.id as string
      );
      navigate(continueBusinessRegistrationRoute);
      return;
    }
    const businessId = businessRowData["id"] as string;
    navigate(
      `${ROUTES.BUSINESSES_PAGE.replace("/:activeStatus", "")}/details/${businessId}`
    );
  };

  const formattedData = useMemo(() => {
    return businesses.map((business) => {
      const {
        id,
        companyData,
        status,
        email,
        profileImage,
        createdAt,
        isActive,
        disabled,
        firstName,
        lastName,
        balance,
      } = business;
      const avatarBg = getNextColor(
        getRandomNumber(START_INDEX, COLORS_MAX_LENGTH_INDEX),
        lastColor
      );
      const hasStartedCompliance =
        companyData?.companyName ||
        companyData?.businessName ||
        companyData?.registrationNumber ||
        companyData?.registrationType;

      let businessType = "default";
      if (
        companyData?.companyType?.toLowerCase() ===
        REGISTERED_BUSINESS_COMPANY_TYPE
      ) {
        businessType = "registered";
      } else if (
        companyData?.companyType?.toLowerCase() ===
        UNREGISTERED_BUSINESS_COMPANY_TYPE
      ) {
        businessType = "unregistered";
      }

      const { color, bg } = BusinessTypeColorMap[businessType.toLowerCase()];
      const isDeactivated = !isActive && disabled;
      const businessActiveStatus =
        activeTextToStatus[
          isActive && status !== "pending" ? "active" : "inactive"
        ];
      let complianceStatus = status;

      if (status === "pending" && hasStartedCompliance) {
        complianceStatus = "started";
      } else if (status === "pending" && !hasStartedCompliance) {
        complianceStatus = "not-started";
      }

      let businessTypeColumnClassName = "";
      switch (businessType.toLowerCase()) {
        case "registered":
          businessTypeColumnClassName = "w-[79px]";
          break;
        case "default":
        case "-":
          businessTypeColumnClassName = "w-fit";
          break;
        default:
          businessTypeColumnClassName = "w-[92px]";
          break;
      }

      return {
        "Business Name": (
          <Box className="flex items-center gap-3">
            {profileImage ? (
              <Box className="w-8 h-8">
                <Image
                  className="w-full h-full rounded-[50%]"
                  src={profileImage}
                />
              </Box>
            ) : (
              <Box
                className="w-8 h-8 rounded-[50%] flex justify-center items-center"
                css={{
                  backgroundColor: avatarBg,
                  color: "#ffffff",
                }}
              >
                <span className="capitalize text-[16px] leading-6 font-medium text-center">
                  {email.charAt(START_INDEX)}
                </span>
              </Box>
            )}
            <Box>
              <Heading4
                fontSize="14px"
                fontWeight="600"
                lineHeight="20px"
                textColor="#09090B"
              >
                {companyData?.companyName || "N/A"}
              </Heading4>
              <Paragraph4
                fontSize="12px"
                lineHeight="16px"
                textColor="#718096"
              >
                {email}
              </Paragraph4>
            </Box>
          </Box>
        ),
        "Owner's Name": (
          <Paragraph3
            lineHeight="20px"
            textColor="#667085"
            className="capitalize"
          >
            {`${firstName} ${lastName}`}
          </Paragraph3>
        ),
        "Business Type": (
          <Box
            className={businessTypeColumnClassName}
            css={{
              ...businessTypeCss,
              backgroundColor: bg,
              color,
              textTransform: "capitalize",
            }}
          >
            {businessType}
          </Box>
        ),
        "Current Balance": (
          <Box css={businessTableColumnCss}>
            {handleAmountFormat({
              value: String(handleTruncateAmount(balance) / ONE_NAIRA_IN_KOBO),
              includeNairaSymbol: true,
              shouldFormatZero: true,
            })}
          </Box>
        ),
        Compliance: (
          <ComplianceStatus
            status={complianceStatus}
            text={statusToTextMap[complianceStatus]}
            className="w-fit"
          />
        ),
        "Date Joined": (
          <Box css={businessTableColumnCss}>
            {formatDateToDDMMMYYYY(createdAt)}
          </Box>
        ),
        Status: (
          <Status
            status={
              isDeactivated ? StatusUiColorEnum.ERROR : businessActiveStatus
            }
            text={
              isDeactivated
                ? "Deactivated"
                : statusToActiveText[businessActiveStatus]
            }
            className="w-fit"
          />
        ),
        "": (
          <Box>
            <ChevronRightIcon />
          </Box>
        ),
        id,
        status,
        businessType,
      };
    });
  }, [businesses]);

  return (
    <TableWrapper className="!p-0">
      <TableHeaderCTAs
        handleGetBusinessesData={handleGetBusinessesData}
        isLoading={isLoadingBusinessesData}
        isRefreshing={isRefreshing}
        handleRefreshTable={handleRefreshTable}
      />
      <Table
        columns={columns}
        data={formattedData}
        onRowClick={handleViewBusinessDetails}
        isLoading={isLoadingBusinessesData && !isRefreshing}
        isRowClickable
        hasCheckbox
        selectedTableRowIds={selectedBusinessIds}
        setSelectedTableRowIds={setSelectedBusinessIds}
        tableRef={tableRef}
      />
      <Pagination
        paginationData={paginationData}
        data={businessesData}
        defaultPaginationParams={BUSINESSES_LIST_TABLE_PAGINATION_PARAMS}
        tableRef={tableRef}
        isLoadingTable={isLoadingBusinessesData}
      />
    </TableWrapper>
  );
};
